import React, { useState, useEffect } from 'react';
import { Plus, Package, Scissors, Calculator, Trash2, Edit2, ShoppingCart, AlertCircle, CheckCircle, LogOut, Sparkles, RefreshCw, Search } from 'lucide-react';

const API_URL = '/api';

const ClairesCrochetApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  const [activeTab, setActiveTab] = useState('calculator');
  const [patterns, setPatterns] = useState([]);
  const [yarns, setYarns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPattern, setEditingPattern] = useState(null);
  const [editingYarn, setEditingYarn] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Daily Picker State
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinningPatterns, setSpinningPatterns] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [rejectionCount, setRejectionCount] = useState(0);
  const [pickHistory, setPickHistory] = useState([]);
  const [rejectLimitReached, setRejectLimitReached] = useState(false);
  const [highlightedPatternId, setHighlightedPatternId] = useState(null);

  const yarnWeights = [
    { id: '0', names: ['0', 'Lace', 'Thread'] },
    { id: '1', names: ['1', 'Super Fine', 'Fingering', 'Sock'] },
    { id: '2', names: ['2', 'Fine', 'Sport', 'Baby'] },
    { id: '3', names: ['3', 'Light', 'DK', 'Light Worsted'] },
    { id: '4', names: ['4', 'Medium', 'Worsted', 'Afghan', 'Aran'] },
    { id: '5', names: ['5', 'Bulky', 'Chunky', 'Craft', 'Rug'] },
    { id: '6', names: ['6', 'Super Bulky', 'Roving'] },
    { id: '7', names: ['7', 'Jumbo'] }
  ];

  const colors = [
    'Red', 'Orange', 'Yellow', 'Green', 'Blue', 'Purple', 
    'Pink', 'Brown', 'Black', 'White', 'Gray', 'Multi-Color'
  ];

  const [patternForm, setPatternForm] = useState({
    name: '',
    yarnWeight: '4',
    hookSize: '',
    yarnAmount: '',
    timeToMake: '',
    colors: [],
    notes: ''
  });

  const [yarnForm, setYarnForm] = useState({
    brand: '',
    skeinWeight: '',
    yarnWeight: '4',
    generalColor: '',
    specificColor: '',
    quantity: 1
  });

  // Product Inventory State
  const [inventory, setInventory] = useState([]);
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [editingInventory, setEditingInventory] = useState(null);
  const [usedColors, setUsedColors] = useState([]);
  
  const [inventoryForm, setInventoryForm] = useState({
    patternId: '',
    patternName: '',
    color: '',
    quantity: 0
  });

  const [calcForm, setCalcForm] = useState({
    patternId: '',
    color: '',
    quantity: 1
  });

  // Check authentication on mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPatterns();
      fetchYarns();
      fetchInventory();
      fetchRejectionCount();
      fetchPickHistory();
    }
  }, [isAuthenticated]);

  const checkAuthentication = async () => {
    try {
      const response = await fetch(`${API_URL}/check-auth`, {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
    }
    setCheckingAuth(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: loginPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIsAuthenticated(true);
        setLoginPassword('');
      } else {
        setLoginError(data.message || 'Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Login failed. Please try again.');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setIsAuthenticated(false);
      setActiveTab('calculator');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const fetchPatterns = async () => {
    try {
      const response = await fetch(`${API_URL}/patterns`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPatterns(data);
      }
    } catch (error) {
      console.error('Error fetching patterns:', error);
    }
  };

  const fetchYarns = async () => {
    try {
      const response = await fetch(`${API_URL}/yarn`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setYarns(data);
      }
    } catch (error) {
      console.error('Error fetching yarns:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await fetch(`${API_URL}/inventory`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInventory(data);
        
        // Extract unique colors for autocomplete
        const colors = [...new Set(data.map(item => item.color))];
        setUsedColors(colors);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchRejectionCount = async () => {
    try {
      const response = await fetch(`${API_URL}/daily-picker/rejection-count`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRejectionCount(data.rejectionCount);
        setRejectLimitReached(data.rejectionCount >= 6);
      }
    } catch (error) {
      console.error('Error fetching rejection count:', error);
    }
  };

  const fetchPickHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/daily-picker/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPickHistory(data);
      }
    } catch (error) {
      console.error('Error fetching pick history:', error);
    }
  };

  const handleAddPattern = async () => {
    setLoading(true);
    try {
      const payload = {
        name: patternForm.name,
        yarnWeight: patternForm.yarnWeight,
        hookSize: patternForm.hookSize,
        yarnAmount: patternForm.yarnAmount,
        timeToMake: patternForm.timeToMake,
        colors: patternForm.colors,
        notes: patternForm.notes
      };

      if (editingPattern !== null) {
        await fetch(`${API_URL}/patterns/${editingPattern}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        setEditingPattern(null);
      } else {
        await fetch(`${API_URL}/patterns`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }
      
      await fetchPatterns();
      setPatternForm({
        name: '',
        yarnWeight: '4',
        hookSize: '',
        yarnAmount: '',
        timeToMake: '',
        colors: [],
        notes: ''
      });
    } catch (error) {
      console.error('Error saving pattern:', error);
    }
    setLoading(false);
  };

  const handleDeletePattern = async (id) => {
    if (window.confirm('Are you sure you want to delete this pattern?')) {
      try {
        await fetch(`${API_URL}/patterns/${id}`, { 
          method: 'DELETE',
          credentials: 'include'
        });
        await fetchPatterns();
      } catch (error) {
        console.error('Error deleting pattern:', error);
      }
    }
  };

  const handleAddYarn = async () => {
    setLoading(true);
    try {
      const payload = {
        brand: yarnForm.brand,
        skeinWeight: yarnForm.skeinWeight,
        yarnWeight: yarnForm.yarnWeight,
        generalColor: yarnForm.generalColor,
        specificColor: yarnForm.specificColor,
        quantity: yarnForm.quantity
      };

      if (editingYarn !== null) {
        await fetch(`${API_URL}/yarn/${editingYarn}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        setEditingYarn(null);
      } else {
        await fetch(`${API_URL}/yarn`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }
      
      await fetchYarns();
      setYarnForm({
        brand: '',
        skeinWeight: '',
        yarnWeight: '4',
        generalColor: '',
        specificColor: '',
        quantity: 1
      });
    } catch (error) {
      console.error('Error saving yarn:', error);
    }
    setLoading(false);
  };

  const handleDeleteYarn = async (id) => {
    if (window.confirm('Are you sure you want to delete this yarn?')) {
      try {
        await fetch(`${API_URL}/yarn/${id}`, { 
          method: 'DELETE',
          credentials: 'include'
        });
        await fetchYarns();
      } catch (error) {
        console.error('Error deleting yarn:', error);
      }
    }
  };

  // PRODUCT INVENTORY FUNCTIONS
  const handleAddInventory = async () => {
    setLoading(true);
    try {
      const payload = {
        pattern_id: inventoryForm.patternId,
        pattern_name: inventoryForm.patternName,
        color: inventoryForm.color.trim(),
        quantity: parseInt(inventoryForm.quantity) || 0
      };

      if (!payload.pattern_name || !payload.color) {
        alert('Please select a pattern and enter a color');
        setLoading(false);
        return;
      }

      if (editingInventory !== null) {
        const response = await fetch(`${API_URL}/inventory/${editingInventory}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            color: payload.color,
            quantity: payload.quantity
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          alert(error.error || 'Error updating inventory');
          setLoading(false);
          return;
        }
        
        setEditingInventory(null);
      } else {
        const response = await fetch(`${API_URL}/inventory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const error = await response.json();
          alert(error.error || 'Error adding inventory');
          setLoading(false);
          return;
        }
      }
      
      await fetchInventory();
      setInventoryForm({
        patternId: '',
        patternName: '',
        color: '',
        quantity: 0
      });
    } catch (error) {
      console.error('Error with inventory:', error);
      alert('Error saving inventory');
    }
    setLoading(false);
  };

  const handleEditInventory = (item) => {
    setEditingInventory(item.id);
    setInventoryForm({
      patternId: item.pattern_id || '',
      patternName: item.pattern_name,
      color: item.color,
      quantity: item.quantity
    });
    // Scroll to form
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleCancelEditInventory = () => {
    setEditingInventory(null);
    setInventoryForm({
      patternId: '',
      patternName: '',
      color: '',
      quantity: 0
    });
  };

  const handleDeleteInventory = async (id, patternName, color) => {
    if (window.confirm(`Permanently delete ${patternName} (${color}) from inventory? This cannot be undone.`)) {
      try {
        await fetch(`${API_URL}/inventory/${id}`, { 
          method: 'DELETE',
          credentials: 'include'
        });
        await fetchInventory();
        
        // If we were editing this item, clear the form
        if (editingInventory === id) {
          handleCancelEditInventory();
        }
      } catch (error) {
        console.error('Error deleting inventory:', error);
      }
    }
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          patternId: calcForm.patternId,
          color: calcForm.color,
          quantity: calcForm.quantity
        })
      });
      const result = await response.json();
      setCalculationResult(result);
    } catch (error) {
      console.error('Error calculating:', error);
    }
    setLoading(false);
  };

  // Daily Picker Functions
  const handlePickPattern = async () => {
    if (rejectLimitReached) return;
    
    setIsSpinning(true);
    setSelectedPattern(null);
    
    try {
      const response = await fetch(`${API_URL}/daily-picker/random`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          setRejectLimitReached(true);
          alert(errorData.message);
        }
        setIsSpinning(false);
        return;
      }
      
      const data = await response.json();
      
      // Create spinning effect with random pattern names
      const spinPatterns = [];
      for (let i = 0; i < 50; i++) {
        const randomPattern = patterns[Math.floor(Math.random() * patterns.length)];
        spinPatterns.push(randomPattern?.name || 'Pattern');
      }
      spinPatterns.push(data.pattern.name);
      setSpinningPatterns(spinPatterns);
      
      // Animate for 15 seconds
      setTimeout(() => {
        setIsSpinning(false);
        setSelectedPattern(data.pattern);
        setRejectionCount(data.rejectionCount);
        if (data.rejectionCount >= 6) {
          setRejectLimitReached(true);
        }
      }, 15000);
      
    } catch (error) {
      console.error('Error picking pattern:', error);
      setIsSpinning(false);
    }
  };

  const handleRejectPattern = () => {
    setSelectedPattern(null);
    handlePickPattern();
  };

  const handleAcceptPattern = async () => {
    if (!selectedPattern) return;
    
    try {
      await fetch(`${API_URL}/daily-picker/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          patternId: selectedPattern.id,
          patternName: selectedPattern.name
        })
      });
      
      // Navigate to patterns tab and highlight the selected pattern
      setHighlightedPatternId(selectedPattern.id);
      setActiveTab('patterns');
      
      // Scroll to the pattern after a brief delay to ensure tab switch completes
      setTimeout(() => {
        const element = document.getElementById(`pattern-${selectedPattern.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Reset picker state
      setSelectedPattern(null);
      setRejectionCount(0);
      setRejectLimitReached(false);
      
      // Refresh history
      fetchPickHistory();
      
      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedPatternId(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error accepting pattern:', error);
    }
  };

  const getYarnWeightDisplay = (weightId) => {
    const weight = yarnWeights.find(w => w.id === weightId);
    return weight ? weight.names.join(' / ') : weightId;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  // Filter patterns based on search term
  const filteredPatterns = patterns.filter(pattern => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const yarnWeightDisplay = getYarnWeightDisplay(pattern.yarn_weight).toLowerCase();
    return (
      pattern.name.toLowerCase().includes(search) ||
      pattern.hook_size.toLowerCase().includes(search) ||
      yarnWeightDisplay.includes(search)
    );
  });

  // Filter inventory based on search term
  const filteredInventory = inventory.filter(item => {
    if (!inventorySearchTerm) return true;
    const search = inventorySearchTerm.toLowerCase();
    return item.pattern_name.toLowerCase().includes(search);
  });

  // Login Screen
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex items-center justify-center">
        <div className="text-purple-600 text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full border border-purple-100">
          <div className="text-center mb-8">
            <div className="text-7xl mb-4">🦄</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Claire's Crochet Calculator</h1>
            <p className="text-purple-600">Please log in to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="Enter password"
                autoFocus
              />
            </div>
            
            {loginError && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !loginPassword}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg relative overflow-hidden">
        {/* Unicorn decorative element */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 text-6xl opacity-20">
          🦄
        </div>
        <div className="max-w-7xl mx-auto px-4 py-6 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-5xl">🦄</div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight">Claire's Crochet Calculator</h1>
                <p className="text-purple-100 mt-2">Manage patterns, track yarn inventory, and calculate project needs</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-all ${
                activeTab === 'calculator'
                  ? 'text-purple-600 border-b-3 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <Calculator size={20} />
              Calculator
            </button>
            <button
              onClick={() => setActiveTab('patterns')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-all ${
                activeTab === 'patterns'
                  ? 'text-purple-600 border-b-3 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <Scissors size={20} />
              Patterns
            </button>
            <button
              onClick={() => setActiveTab('yarn')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-all ${
                activeTab === 'yarn'
                  ? 'text-purple-600 border-b-3 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <Package size={20} />
              Product Inventory
            </button>
            <button
              onClick={() => {
                setActiveTab('dailyPicker');
                fetchRejectionCount();
                fetchPickHistory();
              }}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-all ${
                activeTab === 'dailyPicker'
                  ? 'text-purple-600 border-b-3 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <Sparkles size={20} />
              Daily Picker
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'dailyPicker' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3 justify-center">
                <Sparkles className="text-purple-600" size={32} />
                🎰 Daily Pattern Picker 🎰
                <Sparkles className="text-purple-600" size={32} />
              </h2>
              
              {patterns.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Scissors size={48} className="mx-auto mb-4 text-purple-300" />
                  <p className="text-lg">No patterns yet! Add patterns in the Patterns tab first.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {!isSpinning && !selectedPattern && (
                    <div className="text-center">
                      <button
                        onClick={handlePickPattern}
                        disabled={rejectLimitReached}
                        className="px-12 py-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-2xl font-bold rounded-2xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {rejectLimitReached ? '🔒 Daily Limit Reached' : '✨ Pick Today\'s Pattern! ✨'}
                      </button>
                      <div className="mt-4 text-gray-600">
                        Rejections today: <span className="font-bold text-purple-600">{rejectionCount}/6</span>
                      </div>
                      {rejectLimitReached && (
                        <div className="mt-4 text-orange-600 font-semibold">
                          You've reached your daily limit! Time to commit! 😊
                        </div>
                      )}
                    </div>
                  )}
                  
                  {(isSpinning || selectedPattern) && (
                    <div className="bg-gradient-to-br from-purple-100 to-pink-100 border-4 border-purple-300 rounded-2xl p-8 min-h-[300px] flex flex-col items-center justify-center">
                      {isSpinning ? (
                        <div className="text-center">
                          <div className="text-6xl mb-4 animate-bounce">🎰</div>
                          <div className="text-3xl font-bold text-purple-800 mb-4 animate-pulse">
                            Spinning...
                          </div>
                          <div className="text-xl text-gray-700 h-20 overflow-hidden">
                            <div className="animate-scroll">
                              {spinningPatterns.map((name, idx) => (
                                <div key={idx} className="py-2">{name}</div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : selectedPattern && (
                        <div className="text-center w-full">
                          <div className="text-6xl mb-4">⭐</div>
                          <h3 className="text-4xl font-bold text-purple-900 mb-4">
                            WINNER!
                          </h3>
                          <div className="bg-white rounded-xl p-6 mb-6 shadow-lg">
                            <div className="text-3xl font-bold text-gray-800 mb-4">
                              {selectedPattern.name}
                            </div>
                            <div className="grid md:grid-cols-2 gap-3 text-left text-gray-700">
                              <div><span className="font-semibold">Yarn Weight:</span> {getYarnWeightDisplay(selectedPattern.yarn_weight)}</div>
                              <div><span className="font-semibold">Hook Size:</span> {selectedPattern.hook_size}</div>
                              <div><span className="font-semibold">Yarn Amount:</span> {selectedPattern.yarn_amount} g</div>
                              <div><span className="font-semibold">Time:</span> {selectedPattern.time_to_make} min</div>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 justify-center">
                            <button
                              onClick={handleRejectPattern}
                              disabled={rejectLimitReached}
                              className="flex items-center gap-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <RefreshCw size={20} />
                              Reject & Pick Again
                            </button>
                            <button
                              onClick={handleAcceptPattern}
                              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-lg transition-all shadow-lg"
                            >
                              <CheckCircle size={20} />
                              Start This Project →
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {pickHistory.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  📜 Last 14 Days of Picks
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-purple-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Pattern Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickHistory.map((pick, idx) => (
                        <tr key={idx} className="border-b border-purple-100 hover:bg-purple-50 transition-colors">
                          <td className="py-3 px-4 text-gray-700">{formatDate(pick.pick_date)}</td>
                          <td className="py-3 px-4 text-gray-800 font-medium">{pick.pattern_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'calculator' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Calculator className="text-purple-600" />
                Project Calculator
              </h2>
              
              {patterns.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Scissors size={48} className="mx-auto mb-4 text-purple-300" />
                  <p className="text-lg">No patterns yet! Add patterns in the Patterns tab to get started.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Select Pattern</label>
                      <select
                        value={calcForm.patternId}
                        onChange={(e) => setCalcForm({ ...calcForm, patternId: e.target.value, color: '' })}
                        className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                      >
                        <option value="">Choose a pattern...</option>
                        {patterns.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Color</label>
                      <select
                        value={calcForm.color}
                        onChange={(e) => setCalcForm({ ...calcForm, color: e.target.value })}
                        disabled={!calcForm.patternId}
                        className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">Choose a color...</option>
                        {calcForm.patternId && patterns.find(p => p.id === parseInt(calcForm.patternId))?.colors.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity to Produce</label>
                      <input
                        type="number"
                        min="1"
                        value={calcForm.quantity}
                        onChange={(e) => setCalcForm({ ...calcForm, quantity: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleCalculate}
                    disabled={!calcForm.patternId || !calcForm.color || loading}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Calculating...' : 'Calculate Materials & Time'}
                  </button>
                </div>
              )}
            </div>

            {calculationResult && (
              <>
                <div className={`rounded-2xl shadow-xl p-8 text-white ${
                  calculationResult.adviceType === 'success' ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                  calculationResult.adviceType === 'warning' ? 'bg-gradient-to-br from-yellow-500 to-orange-600' :
                  'bg-gradient-to-br from-red-500 to-pink-600'
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    {calculationResult.adviceType === 'success' ? <CheckCircle size={32} /> : <AlertCircle size={32} />}
                    <h3 className="text-2xl font-bold">{calculationResult.advice}</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-semibold">Available:</span> {calculationResult.inventoryStatus.totalAvailable} g
                    </div>
                    <div>
                      <span className="font-semibold">Required:</span> {calculationResult.yarnNeeded} g
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-6">Production Report</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                      <div className="text-purple-100 text-sm mb-1">Pattern</div>
                      <div className="text-2xl font-bold">{calculationResult.patternName}</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                      <div className="text-purple-100 text-sm mb-1">Quantity</div>
                      <div className="text-2xl font-bold">{calculationResult.quantity} {calculationResult.quantity === 1 ? 'piece' : 'pieces'}</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                      <div className="text-purple-100 text-sm mb-1">Color</div>
                      <div className="text-2xl font-bold">{calculationResult.color}</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                      <div className="text-purple-100 text-sm mb-1">Yarn Needed</div>
                      <div className="text-2xl font-bold">{calculationResult.yarnNeeded} g</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-6 md:col-span-2">
                      <div className="text-purple-100 text-sm mb-1">Estimated Time</div>
                      <div className="text-2xl font-bold">{calculationResult.timeNeeded} minutes</div>
                      {calculationResult.isEarrings && (
                        <div className="text-purple-100 text-sm mt-2 italic">
                          * Time and materials doubled for earring pair
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {calculationResult.inventoryStatus.shoppingList.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                      <ShoppingCart className="text-purple-600" />
                      Shopping List
                    </h3>
                    <div className="space-y-4">
                      {calculationResult.inventoryStatus.shoppingList.map((item, idx) => (
                        <div key={idx} className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
                          {item.brand ? (
                            <>
                              <h4 className="font-bold text-lg text-purple-900">{item.brand}</h4>
                              <div className="mt-2 space-y-1 text-gray-700">
                                <div><span className="font-semibold">Yarn Weight:</span> {item.yarnWeight}</div>
                                <div><span className="font-semibold">Color:</span> {item.color} {item.specificColor && `(${item.specificColor})`}</div>
                                <div><span className="font-semibold">Skeins Needed:</span> {item.skeinsNeeded}</div>
                                <div><span className="font-semibold">Total Grams:</span> {item.ozNeeded} g</div>
                              </div>
                            </>
                          ) : (
                            <>
                              <h4 className="font-bold text-lg text-purple-900">Yarn Needed</h4>
                              <div className="mt-2 space-y-1 text-gray-700">
                                <div><span className="font-semibold">Yarn Weight:</span> {item.yarnWeight}</div>
                                <div><span className="font-semibold">Color:</span> {item.color}</div>
                                <div><span className="font-semibold">Total Grams:</span> {item.ozNeeded} g</div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {calculationResult.inventoryStatus.matchingYarns.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Matching Yarn in Inventory</h3>
                    <div className="space-y-3">
                      {calculationResult.inventoryStatus.matchingYarns.map((yarn) => (
                        <div key={yarn.id} className="border-2 border-purple-100 rounded-lg p-4">
                          <h4 className="font-bold text-lg text-gray-800">{yarn.brand}</h4>
                          <div className="grid md:grid-cols-3 gap-2 mt-2 text-sm text-gray-600">
                            <div><span className="font-semibold">Skein Size:</span> {yarn.skein_weight} g</div>
                            <div><span className="font-semibold">Quantity:</span> {yarn.quantity} skein(s)</div>
                            <div><span className="font-semibold">Total:</span> {(yarn.skein_weight * yarn.quantity).toFixed(2)} g</div>
                          </div>
                          {yarn.specific_color && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-semibold">Color:</span> {yarn.specific_color}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Plus className="text-purple-600" />
                {editingPattern !== null ? 'Edit Pattern' : 'Add New Pattern'}
              </h2>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Pattern Name *</label>
                    <input
                      type="text"
                      value={patternForm.name}
                      onChange={(e) => setPatternForm({ ...patternForm, name: e.target.value })}
                      placeholder="e.g., Flower Coaster, Heart Earrings"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Yarn Weight *</label>
                    <select
                      value={patternForm.yarnWeight}
                      onChange={(e) => setPatternForm({ ...patternForm, yarnWeight: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    >
                      {yarnWeights.map(w => (
                        <option key={w.id} value={w.id}>
                          {w.names.join(' / ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Hook Size *</label>
                    <input
                      type="text"
                      value={patternForm.hookSize}
                      onChange={(e) => setPatternForm({ ...patternForm, hookSize: e.target.value })}
                      placeholder="e.g., H-8 / 5.0mm"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Yarn Amount (grams) *</label>
                    <input
                      type="number"
                      step="0.1"
                      value={patternForm.yarnAmount}
                      onChange={(e) => setPatternForm({ ...patternForm, yarnAmount: e.target.value })}
                      placeholder="e.g., 2.5"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Time to Make (minutes) *</label>
                    <input
                      type="number"
                      step="0.25"
                      value={patternForm.timeToMake}
                      onChange={(e) => setPatternForm({ ...patternForm, timeToMake: e.target.value })}
                      placeholder="e.g., 3.5"
                      className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Available Colors *</label>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {colors.map(color => (
                      <label key={color} className="flex items-center gap-2 p-3 border-2 border-purple-200 rounded-lg cursor-pointer hover:bg-purple-50 transition-all">
                        <input
                          type="checkbox"
                          checked={patternForm.colors.includes(color)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPatternForm({ ...patternForm, colors: [...patternForm.colors, color] });
                            } else {
                              setPatternForm({ ...patternForm, colors: patternForm.colors.filter(c => c !== color) });
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium">{color}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes (Optional)</label>
                  <textarea
                    value={patternForm.notes}
                    onChange={(e) => setPatternForm({ ...patternForm, notes: e.target.value })}
                    placeholder="Any additional notes about this pattern..."
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                  />
                </div>

                <button
                  onClick={handleAddPattern}
                  disabled={!patternForm.name || !patternForm.hookSize || !patternForm.yarnAmount || !patternForm.timeToMake || patternForm.colors.length === 0 || loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : editingPattern !== null ? 'Update Pattern' : 'Add Pattern'}
                </button>

                {editingPattern !== null && (
                  <button
                    onClick={() => {
                      setEditingPattern(null);
                      setPatternForm({
                        name: '',
                        yarnWeight: '4',
                        hookSize: '',
                        yarnAmount: '',
                        timeToMake: '',
                        colors: [],
                        notes: ''
                      });
                    }}
                    className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

            {patterns.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search patterns..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-4">Saved Patterns</h3>
                {filteredPatterns.length === 0 && searchTerm ? (
                  <div className="text-center py-8 text-gray-500">
                    No patterns found matching "{searchTerm}"
                  </div>
                ) : (
                <div className="space-y-3">
                  {filteredPatterns.map((pattern) => (
                    <div 
                      key={pattern.id} 
                      id={`pattern-${pattern.id}`}
                      className={`border-2 rounded-lg p-4 transition-all ${
                        highlightedPatternId === pattern.id 
                          ? 'border-yellow-400 bg-yellow-50 shadow-lg' 
                          : 'border-purple-100 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-bold text-lg text-gray-800">{pattern.name}</h4>
                          <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
                            <div><span className="font-semibold">Yarn:</span> {getYarnWeightDisplay(pattern.yarn_weight)}</div>
                            <div><span className="font-semibold">Hook:</span> {pattern.hook_size}</div>
                            <div><span className="font-semibold">Amount:</span> {pattern.yarn_amount} g</div>
                            <div><span className="font-semibold">Time:</span> {pattern.time_to_make} min</div>
                          </div>
                          <div className="mt-2">
                            <span className="text-sm font-semibold text-gray-700">Colors: </span>
                            <span className="text-sm text-gray-600">{pattern.colors.join(', ')}</span>
                          </div>
                          {pattern.notes && (
                            <div className="mt-2 text-sm text-gray-600 italic whitespace-pre-wrap">{pattern.notes}</div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => {
                              setPatternForm({
                                name: pattern.name,
                                yarnWeight: pattern.yarn_weight,
                                hookSize: pattern.hook_size,
                                yarnAmount: pattern.yarn_amount,
                                timeToMake: pattern.time_to_make,
                                colors: pattern.colors,
                                notes: pattern.notes || ''
                              });
                              setEditingPattern(pattern.id);
                            }}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDeletePattern(pattern.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
          </div>
        )}


        {activeTab === 'yarn' && (
          <div className="space-y-6">
            {/* Search Box */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={inventorySearchTerm}
                  onChange={(e) => setInventorySearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-lg border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                />
              </div>
            </div>

            {/* Inventory Table */}
            {filteredInventory.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Current Inventory</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-purple-200">
                        <th className="text-left py-4 px-4 text-lg font-bold text-gray-700">Product Name</th>
                        <th className="text-left py-4 px-4 text-lg font-bold text-gray-700">Color</th>
                        <th className="text-left py-4 px-4 text-lg font-bold text-gray-700">Quantity</th>
                        <th className="text-left py-4 px-4 text-lg font-bold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item, index) => (
                        <React.Fragment key={item.id}>
                          <tr className="hover:bg-purple-50 transition-colors">
                            <td className="py-4 px-4 text-lg text-gray-800">{item.pattern_name}</td>
                            <td className="py-4 px-4 text-lg text-gray-600">{item.color}</td>
                            <td className="py-4 px-4 text-lg font-semibold text-gray-800">{item.quantity}</td>
                            <td className="py-4 px-4">
                              <button
                                onClick={() => handleEditInventory(item)}
                                className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-all"
                                title="Edit"
                              >
                                <Edit2 size={20} />
                              </button>
                            </td>
                          </tr>
                          {index < filteredInventory.length - 1 && (
                            <tr>
                              <td colSpan="4" className="py-0">
                                <div className="border-b border-gray-200"></div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Add/Edit Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Plus className="text-purple-600" />
                {editingInventory !== null ? 'Edit Inventory Item' : 'Add Product to Inventory'}
              </h2>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-2">Product/Pattern *</label>
                    <select
                      value={inventoryForm.patternId}
                      onChange={(e) => {
                        const selectedPattern = patterns.find(p => p.id === parseInt(e.target.value));
                        setInventoryForm({
                          ...inventoryForm,
                          patternId: e.target.value,
                          patternName: selectedPattern ? selectedPattern.name : ''
                        });
                      }}
                      disabled={editingInventory !== null}
                      className="w-full px-4 py-3 text-lg border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all disabled:bg-gray-100"
                    >
                      <option value="">Select a pattern...</option>
                      {patterns.map(pattern => (
                        <option key={pattern.id} value={pattern.id}>
                          {pattern.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-2">Color *</label>
                    <input
                      type="text"
                      value={inventoryForm.color}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, color: e.target.value })}
                      placeholder="e.g., Pink, Red/White, Multi-Color"
                      list="color-suggestions"
                      className="w-full px-4 py-3 text-lg border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                    <datalist id="color-suggestions">
                      {usedColors.map((color, index) => (
                        <option key={index} value={color} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-2">Quantity *</label>
                    <input
                      type="number"
                      min="0"
                      value={inventoryForm.quantity}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })}
                      placeholder="0"
                      className="w-full px-4 py-3 text-lg border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleAddInventory}
                    disabled={loading}
                    className="flex-1 bg-purple-600 text-white text-lg py-4 rounded-lg font-semibold hover:bg-purple-700 transition-all disabled:bg-gray-400"
                  >
                    {loading ? 'Saving...' : (editingInventory !== null ? 'Update Inventory' : 'Add to Inventory')}
                  </button>
                  
                  {editingInventory !== null && (
                    <button
                      onClick={handleCancelEditInventory}
                      className="flex-1 bg-gray-300 text-gray-700 text-lg py-4 rounded-lg font-semibold hover:bg-gray-400 transition-all"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {editingInventory !== null && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleDeleteInventory(editingInventory, inventoryForm.patternName, inventoryForm.color)}
                      className="text-red-600 text-lg hover:text-red-700 font-medium hover:underline"
                    >
                      Delete This Item
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes scroll {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100%); }
        }
        .animate-scroll {
          animation: scroll 2s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ClairesCrochetApp;
